import { type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { pathMatchesItem } from "@/config/sidebar-nav";
import { Badge } from "@/components/ui/badge";

/**
 * A single flat nav row. The redesigned sidebar (1c) has no collapsible
 * parents: every feature is a leaf link, so nothing takes two clicks.
 */
export type NavMainItem = {
  title: string;
  url: string;
  icon?: LucideIcon;
  /** Shown next to the title (e.g. Beta). */
  titleBadge?: string;
  /** Pre-computed active state; falls back to a prefix match on `url`. */
  isActive?: boolean;
};

function NavTitleWithOptionalBadge({
  title,
  titleBadge,
}: {
  title: string;
  titleBadge?: string;
}) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1.5">
      <span className="truncate">{title}</span>
      {titleBadge ? (
        <Badge
          variant="secondary"
          className="h-5 shrink-0 px-1.5 py-0 text-[10px] font-medium"
        >
          {titleBadge}
        </Badge>
      ) : null}
    </span>
  );
}

export function NavMain({
  items,
  pathname,
  groupLabel = null,
}: {
  items: NavMainItem[];
  pathname: string;
  /** Optional uppercase group heading (HIRING, TEAM). */
  groupLabel?: string | null;
}) {
  return (
    <SidebarGroup>
      {groupLabel ? <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel> : null}
      <SidebarMenu>
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.isActive ?? pathMatchesItem(pathname, item.url);
          const tooltip = item.titleBadge
            ? `${item.title} (${item.titleBadge})`
            : item.title;
          return (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton tooltip={tooltip} isActive={active} asChild>
                <Link to={item.url} aria-current={active ? "page" : undefined}>
                  {Icon ? <Icon aria-hidden="true" /> : null}
                  <NavTitleWithOptionalBadge
                    title={item.title}
                    titleBadge={item.titleBadge}
                  />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
